/**
 * @fileoverview Shared tokens + highlighting for report-template wording.
 *
 * The section wording carries three kinds of inline tokens:
 * - `{{Liquidator name}}` merge fields (free-form labels, spaces allowed —
 *   unlike the snake_case precedent keys) → amber chips.
 * - `[Table with fixed columns: …]` structural directives → slate chips.
 * - Phrases quoted in an adaptable section's adaptation notes → violet underline.
 *
 * Used by the template editor's styled views, the plain-textarea backdrop,
 * and the rich wording editor's decoration plugin.
 */

import type { ReactNode } from "react";

export const TEMPLATE_TOKEN_RE = /(\{\{[^{}\n]+\}\})|(\[[^\][]+\])/g;

export const MERGE_FIELD_CHIP =
  "rounded-[4px] bg-amber-200/80 shadow-[inset_0_0_0_1px_rgba(245,158,11,0.55)] dark:bg-amber-400/25 dark:shadow-[inset_0_0_0_1px_rgba(245,158,11,0.35)]";
export const DIRECTIVE_CHIP =
  "rounded-[4px] bg-slate-200/70 shadow-[inset_0_0_0_1px_rgba(100,116,139,0.4)] dark:bg-slate-400/20 dark:shadow-[inset_0_0_0_1px_rgba(148,163,184,0.35)]";
/** Quoted phrases in the wording — a violet underline, not a tight badge. */
export const FLEX_PHRASE_CHIP =
  "underline decoration-2 decoration-violet-500 underline-offset-[3px] dark:decoration-violet-400";
/** Matching quoted-phrase pill under the adaptation notes field. */
export const FLEX_PHRASE_PILL =
  "rounded-[4px] bg-violet-200/60 px-1.5 py-0.5 shadow-[inset_0_0_0_1px_rgba(139,92,246,0.4)] dark:bg-violet-400/20 dark:shadow-[inset_0_0_0_1px_rgba(167,139,250,0.35)]";
/** `{{auto:…}}` system inserts (TOC, annexures, date) — filled in code. */
export const AUTO_INSERT_CHIP =
  "rounded-[4px] bg-emerald-200/70 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.5)] dark:bg-emerald-400/20 dark:shadow-[inset_0_0_0_1px_rgba(52,211,153,0.35)]";
/** @-tagged data sources (agents / investigations) in the wording. */
export const DATA_TAG_CHIP =
  "rounded-[4px] bg-teal-100 px-1 text-teal-900 shadow-[inset_0_0_0_1px_rgba(13,148,136,0.45)] dark:bg-teal-400/20 dark:text-teal-100 dark:shadow-[inset_0_0_0_1px_rgba(45,212,191,0.35)]";
/** Passages the user highlighted for the assistant (persistent marks). */
export const SELECTED_PASSAGE_CHIP =
  "rounded-[4px] bg-primary/15 shadow-[inset_0_0_0_1px_rgba(20,60,40,0.25)] dark:bg-primary/25 dark:shadow-[inset_0_0_0_1px_rgba(120,180,150,0.3)]";

/** Practitioner merge field (`{{Liquidator name}}`), not a `{{auto:…}}` insert. */
export function isUserMergeField(token: string): boolean {
  return token.startsWith("{{") && !/^\{\{\s*auto\s*:/i.test(token);
}

/** Chip class for a `{{field}}` / `{{auto:x}}` / `[directive]` token. */
export function tokenChipClass(token: string): string {
  if (token.startsWith("{{")) {
    return isUserMergeField(token) ? MERGE_FIELD_CHIP : AUTO_INSERT_CHIP;
  }
  return DIRECTIVE_CHIP;
}

/** A phrase to mark in the wording, with the chip class to mark it with. */
export interface PhraseMark {
  phrase: string;
  className: string;
}

/** Does a `[directive]` in the wording already place a table / chart? */
export const TABLE_DIRECTIVE_RE = /\[[^\][]*\btable\b[^\][]*\]/i;
export const CHART_DIRECTIVE_RE = /\[[^\][]*(?:chart|graph)[^\][]*\]/i;

/**
 * `[Table of contents …]` is an auto-insert (`{{auto:toc}}`), not a generated
 * data table. Without this, the preview swapped it for a table skeleton even
 * when the Table content-type chip was off.
 */
const TOC_DIRECTIVE_RE =
  /table\s+of\s+contents|contents\s+(?:table|list|page)/i;

/** True for a data-table `[Table with fixed columns: …]` directive. */
export function isDataTableDirective(text: string): boolean {
  return TABLE_DIRECTIVE_RE.test(text) && !TOC_DIRECTIVE_RE.test(text);
}

/** Pull the column names out of "[Table with fixed columns: A, B, C; …]". */
export function parseDirectiveColumns(directive: string): string[] | null {
  const m = /columns?\s*:\s*([^\];]+)/i.exec(directive);
  if (!m) return null;
  const cols = m[1]
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
  return cols.length > 1 ? cols : null;
}

/**
 * Guidance saved by the rich wording editor is HTML; older sections (and
 * extraction output) are plain text with newlines.
 */
export function looksLikeHtml(s: string): boolean {
  return /<\/?(p|ul|ol|li|strong|em|u|b|i|br|s|div|table|thead|tbody|tr|th|td)\b/i.test(
    s,
  );
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Lift legacy plain-text wording into the rich editor's paragraph model. */
export function plainTextToHtml(text: string): string {
  return text
    .split("\n")
    .map((line) => {
      const t = line.trim();
      return t ? `<p>${escapeHtml(t)}</p>` : "<p></p>";
    })
    .join("");
}

/**
 * Phrases the practitioner put in double quotes inside the adaptation notes —
 * the parts of the wording explicitly flagged to flex. Straight `"` and curly
 * `“ ”` both count; single quotes do not (they collide with apostrophes).
 */
export function extractQuotedPhrases(notes: string | undefined): string[] {
  if (!notes) return [];
  const phrases: string[] = [];
  for (const m of notes.matchAll(/"([^"]{3,80})"|“([^”]{3,80})”/g)) {
    const p = (m[1] ?? m[2])?.trim();
    if (p) phrases.push(p);
  }
  return phrases;
}

/** Case-insensitively wrap occurrences of marked phrases in their chips. */
export function highlightPhrases(
  text: string,
  marks: PhraseMark[],
  keyPrefix: string,
): ReactNode[] {
  if (marks.length === 0 || !text) return [text];
  const lower = text.toLowerCase();
  const nodes: ReactNode[] = [];
  let pos = 0;
  let k = 0;
  while (pos < text.length) {
    let best: { at: number; len: number; className: string } | null = null;
    for (const m of marks) {
      const at = lower.indexOf(m.phrase.toLowerCase(), pos);
      if (at !== -1 && (!best || at < best.at))
        best = { at, len: m.phrase.length, className: m.className };
    }
    if (!best) break;
    if (best.at > pos) nodes.push(text.slice(pos, best.at));
    nodes.push(
      <span key={`${keyPrefix}-${k++}`} className={best.className}>
        {text.slice(best.at, best.at + best.len)}
      </span>,
    );
    pos = best.at + best.len;
  }
  if (pos < text.length) nodes.push(text.slice(pos));
  return nodes;
}

/**
 * Render a run of wording text with `{{merge fields}}` amber, `[directives]`
 * slate, and (for adaptable sections) note-quoted phrases violet. In the
 * plain-textarea backdrop the text is transparent so only the chip
 * backgrounds show; in styled views the inherited colour makes them visible.
 */
export function renderMergeFieldHighlights(
  value: string,
  flexPhrases: string[] = [],
  selectedPhrases: string[] = [],
  onMergeFieldClick?: (token: string) => void,
): ReactNode[] {
  const marks: PhraseMark[] = [
    ...flexPhrases.map((phrase) => ({ phrase, className: FLEX_PHRASE_CHIP })),
    ...selectedPhrases.map((phrase) => ({
      phrase,
      className: SELECTED_PASSAGE_CHIP,
    })),
  ];
  const nodes: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const match of value.matchAll(TEMPLATE_TOKEN_RE)) {
    const start = match.index ?? 0;
    if (start > last)
      nodes.push(...highlightPhrases(value.slice(last, start), marks, `f${i}`));
    const token = match[0];
    const clickable = !!onMergeFieldClick && isUserMergeField(token);
    nodes.push(
      <span
        key={i++}
        className={
          clickable
            ? `${tokenChipClass(token)} cursor-pointer hover:bg-amber-300/90 dark:hover:bg-amber-400/40`
            : tokenChipClass(token)
        }
        title={clickable ? "Add to adaptation notes" : undefined}
        onMouseDown={
          clickable
            ? (e) => {
                e.stopPropagation();
                e.preventDefault();
              }
            : undefined
        }
        onMouseUp={
          clickable
            ? (e) => {
                e.stopPropagation();
                e.preventDefault();
              }
            : undefined
        }
        onClick={
          clickable
            ? (e) => {
                e.stopPropagation();
                onMergeFieldClick(token);
              }
            : undefined
        }
      >
        {token}
      </span>,
    );
    last = start + token.length;
  }
  if (last < value.length)
    nodes.push(...highlightPhrases(value.slice(last), marks, "ftail"));
  return nodes;
}
